import { dayjs } from '../../libs/day';
import { DEFAULT_SEARCH_PARAMETERS } from './constants';
import { ISearchParameters } from './types';

/**
 * @description LocalStorage key used to persist the TravelSearchBar form state
 * across visits.
 */
export const SEARCH_PARAMETERS_STORAGE_KEY = 'tictactrip:searchParameters';

/**
 * @description Safely access window.localStorage. Returns null when the
 * environment does not provide it (SSR, private mode, disabled storage).
 */
const getStorage = (): Storage | null => {
  try {
    if (typeof window === 'undefined') return null;

    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * @description Replaces dates that are now in the past with safe defaults so
 * that the persisted form does not violate the searchParamsSchema validation.
 *
 * Rules:
 *  - If the persisted outboundDate is missing, invalid or in the past, fall
 *    back to today's date.
 *  - If the persisted returnDate is missing, invalid or strictly before the
 *    (potentially adjusted) outboundDate, drop it.
 */
const sanitizeDates = (
  parameters: Partial<ISearchParameters>,
): Partial<ISearchParameters> => {
  const sanitized: Partial<ISearchParameters> = { ...parameters };
  const today = dayjs();

  if (
    !sanitized.outboundDate ||
    !dayjs(sanitized.outboundDate).isValid() ||
    dayjs(sanitized.outboundDate).isBefore(today, 'day')
  ) {
    sanitized.outboundDate = today.format('YYYY-MM-DD');
    sanitized.outboundTime = null;
  }

  if (
    sanitized.returnDate &&
    (!dayjs(sanitized.returnDate).isValid() ||
      dayjs(sanitized.returnDate).isBefore(
        dayjs(sanitized.outboundDate),
        'day',
      ))
  ) {
    sanitized.returnDate = null;
    sanitized.returnTime = null;
  }

  return sanitized;
};

/**
 * @description Reads the persisted search parameters from localStorage and
 * merges them on top of DEFAULT_SEARCH_PARAMETERS. Falls back to the defaults
 * when the storage is unavailable, empty or contains invalid data.
 */
export const loadStoredSearchParameters = (): ISearchParameters => {
  const storage = getStorage();

  if (!storage) return DEFAULT_SEARCH_PARAMETERS;

  try {
    const raw = storage.getItem(SEARCH_PARAMETERS_STORAGE_KEY);

    if (!raw) return DEFAULT_SEARCH_PARAMETERS;

    const parsed = JSON.parse(raw) as Partial<ISearchParameters> | null;

    if (!parsed || typeof parsed !== 'object') {
      return DEFAULT_SEARCH_PARAMETERS;
    }

    const sanitized = sanitizeDates(parsed);

    return {
      ...DEFAULT_SEARCH_PARAMETERS,
      ...sanitized,
      passengers:
        Array.isArray(sanitized.passengers) && sanitized.passengers.length > 0
          ? sanitized.passengers
          : DEFAULT_SEARCH_PARAMETERS.passengers,
    };
  } catch {
    return DEFAULT_SEARCH_PARAMETERS;
  }
};

/**
 * @description Persists the search parameters to localStorage. Silently fails
 * when storage is unavailable so the form keeps working.
 */
export const saveSearchParameters = (
  searchParameters: ISearchParameters | null,
): void => {
  const storage = getStorage();

  if (!storage || !searchParameters) return;

  try {
    storage.setItem(
      SEARCH_PARAMETERS_STORAGE_KEY,
      JSON.stringify(searchParameters),
    );
  } catch {
    /* swallow quota / serialization errors */
  }
};
